import {
  useAvailableYears,
  useClinicExcelExport,
  useMonthlySummary,
  useYearlyPerformance,
} from '@/entities/performance/hooks/usePerformanceQueries';
import { monthOf } from '@/entities/performance/lib/performance';
import { getApiErrorCode, isNetworkError } from '@/shared/api/http-client';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { toUrlYm } from '@/shared/utils/format';
import { toastApi } from '@/shared/utils/toast';
import { Link, useNavigate } from '@tanstack/react-router';
import { ExcelExportButton, PERF_BADGE, PerfBadge } from './PerformanceParts';

/** "202607" 기준 ±1달 */
const shiftYm = (ym: string, diff: number) => {
  const d = new Date(Number(ym.slice(0, 4)), monthOf(ym) - 1 + diff, 1);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** presigned URL은 Content-Disposition(attachment)으로 오므로 링크 클릭만으로 저장된다 */
const downloadFile = (url: string, fileName: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/** 모바일(<1024)은 목록 단계에서 병의원이 선택되지 않은 상태다 (README §8) */
const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

/**
 * M-09 헤더 — [← 실적] · "YYYY년 M월 실적" · 월 이동 · 정산 비율 · 엑셀
 * 조회 범위 밖(가입 전 · 현재 정산월 이후)으로는 이동할 수 없다.
 * 같은 해는 /yearly 월 카드(현재 정산월까지만 온다), 해를 넘으면 available-years로 판단한다.
 * 엑셀은 지금 보고 있는 병의원 하나 · 정산월 단위다 (월 전체 엑셀은 없다).
 */
export const MonthHeader = ({
  ym,
  clinicId,
  clinicName,
  clinicSelected,
}: {
  ym: string;
  /** 상세에 보이는 병의원 — 데스크톱은 URL에 없으면 첫 병의원 */
  clinicId?: number;
  clinicName?: string;
  /** URL로 병의원을 고른 상태인지 — 모바일 목록 단계 구분용 */
  clinicSelected: boolean;
}) => {
  const navigate = useNavigate();
  const excel = useClinicExcelExport();
  const year = Number(ym.slice(0, 4));
  const { data: years } = useAvailableYears();
  const { data: yearly } = useYearlyPerformance(year);
  const { data: monthly } = useMonthlySummary(ym);

  const prev = shiftYm(ym, -1);
  const next = shiftYm(ym, 1);
  const canGo = (target: string) => {
    const targetYear = Number(target.slice(0, 4));
    if (targetYear !== year) return !!years?.includes(targetYear);
    return !!yearly?.months.some((m) => m.settlementYearMonth === target);
  };

  const go = (target: string) =>
    void navigate({
      to: ROUTE_PATHS.PERFORMANCE_MONTH,
      params: { yearMonth: toUrlYm(target) },
      search: (prevSearch: Record<string, unknown>) => ({
        clinicId: prevSearch.clinicId as number | undefined,
        tab: prevSearch.tab as 'all' | 'images' | 'pharma' | undefined,
      }),
    });

  const exportExcel = () => {
    if (clinicId === undefined || (!clinicSelected && !isDesktop())) {
      toastApi.info('병의원을 먼저 선택해주세요.');
      return;
    }
    excel.mutate(
      { month: ym, clinicId },
      {
        onSuccess: ({ downloadUrl, fileName }) =>
          downloadFile(downloadUrl, fileName),
        onError: (error) => {
          // 네트워크 오류는 http-client가 공통 스낵바를 띄운다
          if (isNetworkError(error)) return;
          if (getApiErrorCode(error) === 'WEB_PERFORMANCE_EXPORT_EMPTY') {
            toastApi.info('내보낼 내역이 없습니다.');
            return;
          }
          toastApi.error('엑셀을 만들지 못했어요. 잠시 후 다시 시도해주세요.');
        },
      },
    );
  };

  const stepClass =
    'px-3 py-[9px] text-[13px] leading-5 text-ink-3 disabled:cursor-not-allowed disabled:text-ink-faint lg:px-3.5';

  return (
    <div className="flex flex-col gap-3 lg:h-12 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
        <Link
          to={ROUTE_PATHS.PERFORMANCE}
          search={{ year }}
          className="text-[13.5px] leading-5 font-medium text-[#767676] hover:text-ink"
        >
          ← 실적
        </Link>
        <h1 className="text-[22px] leading-[33px] font-bold text-ink lg:text-[26px] lg:leading-[39px]">
          {year}년 {monthOf(ym)}월 실적
        </h1>
        <div className="flex overflow-hidden rounded-[9px] border border-[#dbdbdb] bg-surface">
          <button
            type="button"
            className={stepClass}
            disabled={!canGo(prev)}
            onClick={() => go(prev)}
          >
            ‹ {monthOf(prev)}월
          </button>
          <span className="bg-ink px-3 py-[9px] text-[13px] leading-5 font-bold text-white lg:px-3.5">
            {monthOf(ym)}월
          </span>
          <button
            type="button"
            className={stepClass}
            disabled={!canGo(next)}
            onClick={() => go(next)}
          >
            {monthOf(next)}월 ›
          </button>
        </div>
        {monthly?.hasSettlement && monthly.settlementRatio !== null && (
          <PerfBadge className={cn(PERF_BADGE.settled)}>
            정산 비율 {Math.round(monthly.settlementRatio)}%
          </PerfBadge>
        )}
      </div>
      <ExcelExportButton
        onClick={clinicId === undefined ? undefined : exportExcel}
        pending={excel.isPending}
        title={clinicName ? `${clinicName} 실적 엑셀 내보내기` : undefined}
      />
    </div>
  );
};
